/**
 * Pure display helpers for the super-admin platform billing surface. Money goes
 * through the one deterministic {@link formatUsd}; this file only adds the
 * percentage + tone derivations the money helper doesn't cover. Framework-free
 * so it's trivially unit-testable and reusable by both the totals band and the
 * per-tenant table.
 */

import { formatUsd } from "../../../lib/format-money"

/** Semantic tone for a profit figure — success when in the black, danger in the red. */
export type TProfitTone = "profit" | "loss" | "neutral"

/**
 * Derive the profit tone from a margin amount. Exactly zero is neutral (nothing
 * earned, nothing lost); positive is profit; negative is a loss.
 */
export function profitTone(marginUsd: number): TProfitTone {
  if (!Number.isFinite(marginUsd) || marginUsd === 0) return "neutral"
  return marginUsd > 0 ? "profit" : "loss"
}

/**
 * Format a margin percentage, e.g. `42.3%` / `-8.0%`. Non-finite (e.g. revenue
 * was 0, so pct is undefined) renders as an em dash rather than `NaN%`.
 */
export function formatPct(value: number): string {
  if (!Number.isFinite(value)) return "—"
  return `${value.toFixed(1)}%`
}

/** Re-export so consumers import both money + pct formatting from one place. */
export { formatUsd }
